/**
 * Marketing lead API client — typed wrapper over app/api/v1/leads.py.
 *
 *   POST   /leads                 capture
 *   GET    /leads                 list (status/assigned_to filters, score-ranked)
 *   GET    /leads/metrics         lead-quality metrics
 *   GET    /leads/{id}            detail + engagement timeline
 *   POST   /leads/{id}/activity   record a scored engagement
 *   POST   /leads/{id}/assign     sales handoff
 *   POST   /leads/{id}/status     lifecycle transition
 *   GET/POST /leads/rules         scoring rules
 */

import { apiClient } from "@/api/client";

export type LeadStatus = "NEW" | "QUALIFIED" | "CONTACTED" | "CONVERTED" | "LOST";
export const LEAD_STATUSES: LeadStatus[] = ["NEW", "QUALIFIED", "CONTACTED", "CONVERTED", "LOST"];

export interface Lead {
  id: string;
  org_id: string;
  campaign_id: string | null;
  source: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  company: string | null;
  status: LeadStatus;
  score: number;
  assigned_to: string | null;
  attributes: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface LeadTimelineEntry {
  activity_type: string;
  points_delta: number;
  detail: Record<string, unknown>;
  created_at: string;
}

export interface LeadDetail extends Lead {
  timeline: LeadTimelineEntry[];
}

export interface LeadMetrics {
  total: number;
  by_status: Record<string, { count: number; avg_score: number }>;
  qualified_or_better: number;
  conversion_rate: number;
}

export interface LeadScoreRule {
  id: string;
  name: string;
  event_type: string;
  points: number;
  is_active: boolean;
}

export interface CaptureLeadInput {
  source?: string;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  company?: string | null;
  campaign_id?: string | null;
  attributes?: Record<string, unknown>;
}

export interface LeadListResult {
  items: Lead[];
  total: number;
  limit: number;
  offset: number;
}

export const leadsApi = {
  capture(body: CaptureLeadInput): Promise<Lead> {
    return apiClient.post<Lead>("/leads", body);
  },
  list(params?: { status?: LeadStatus; assigned_to?: string; limit?: number; offset?: number }): Promise<LeadListResult> {
    return apiClient.get<LeadListResult>("/leads", params);
  },
  metrics(): Promise<LeadMetrics> {
    return apiClient.get<LeadMetrics>("/leads/metrics");
  },
  get(id: string): Promise<LeadDetail> {
    return apiClient.get<LeadDetail>(`/leads/${id}`);
  },
  recordActivity(id: string, activity_type: string, detail: Record<string, unknown> = {}): Promise<Lead> {
    return apiClient.post<Lead>(`/leads/${id}/activity`, { activity_type, detail });
  },
  assign(id: string, user_id: string): Promise<Lead> {
    return apiClient.post<Lead>(`/leads/${id}/assign`, { user_id });
  },
  setStatus(id: string, status: LeadStatus): Promise<Lead> {
    return apiClient.post<Lead>(`/leads/${id}/status`, { status });
  },
  listRules(): Promise<{ rules: LeadScoreRule[] }> {
    return apiClient.get<{ rules: LeadScoreRule[] }>("/leads/rules");
  },
  createRule(body: { name: string; event_type: string; points: number }): Promise<LeadScoreRule> {
    return apiClient.post<LeadScoreRule>("/leads/rules", body);
  },
};
